import { gql, useQuery } from '@apollo/client';
import * as React from 'react';

import type {
  GetBankConnectionsSyncQuery,
  GetBankConnectionsSyncQueryVariables,
} from './__generated__/useBankConnectionSync.gql';

const GET_BANK_CONNECTIONS_SYNC = gql`
  query GetBankConnectionsSync {
    getBankConnectionsForUser {
      id
      lastSyncedAt
      historicalSyncComplete
      hasPotentialSyncIssue
    }
  }
`;

// Polling intervals in milliseconds
const INITIAL_POLL_INTERVAL = 5 * 1000; // 5 seconds
const BACKOFF_POLL_INTERVAL = 10 * 60 * 1000; // 10 minutes
const INITIAL_POLL_DURATION = 2 * 60 * 1000; // 2 minutes
const MAX_POLL_DURATION = 24 * 60 * 60 * 1000; // 24 hours

export const useBankConnectionSync = () => {
  const [pollStartTime, setPollStartTime] = React.useState<number | null>(null);
  const [pollInterval, setPollInterval] = React.useState<number | undefined>(undefined);
  const hasBackedOffRef = React.useRef(false);
  const hasHitMaxDurationRef = React.useRef(false);

  const { data, stopPolling } = useQuery<GetBankConnectionsSyncQuery, GetBankConnectionsSyncQueryVariables>(
    GET_BANK_CONNECTIONS_SYNC,
    {
      pollInterval,
      fetchPolicy: 'network-only',
    }
  );

  const bankConnections = data?.getBankConnectionsForUser ?? [];
  const hasUnsyncedConnections = bankConnections.some((connection) => connection.lastSyncedAt === null);
  const hasIncompleteHistoricalSync = bankConnections.some((connection) => !connection.historicalSyncComplete);
  const isSyncing = hasUnsyncedConnections || hasIncompleteHistoricalSync;

  const hasPotentialSyncIssue = bankConnections.some((connection) => connection.hasPotentialSyncIssue);

  // Initialize polling when we detect unsynced connections
  React.useEffect(() => {
    if (isSyncing && pollStartTime === null && !hasHitMaxDurationRef.current) {
      // Start polling with initial interval
      const startTime = Date.now();
      setPollStartTime(startTime);
      setPollInterval(INITIAL_POLL_INTERVAL);
      hasBackedOffRef.current = false;
    } else if (!isSyncing && pollStartTime !== null) {
      // Stop polling when all connections are synced
      stopPolling();
      setPollStartTime(null);
      setPollInterval(undefined);
      hasBackedOffRef.current = false;
      hasHitMaxDurationRef.current = false; // Reset so we can poll again if new unsynced connections appear
    }
  }, [isSyncing, pollStartTime, stopPolling]);

  // Manage polling interval based on elapsed time
  // Check elapsed time whenever we get new data or when polling starts
  React.useEffect(() => {
    if (pollStartTime === null || !isSyncing) {
      return;
    }

    const checkElapsedTime = () => {
      const elapsed = Date.now() - pollStartTime;

      // After initial duration, back off to longer intervals
      if (elapsed >= INITIAL_POLL_DURATION && !hasBackedOffRef.current) {
        hasBackedOffRef.current = true;
        setPollInterval(BACKOFF_POLL_INTERVAL);
      }

      // Stop polling after max duration
      if (elapsed >= MAX_POLL_DURATION) {
        stopPolling();
        setPollStartTime(null);
        setPollInterval(undefined);
        hasBackedOffRef.current = false;
        hasHitMaxDurationRef.current = true; // Prevent restarting
      }
    };

    // Check immediately
    checkElapsedTime();

    // Set up interval to check elapsed time periodically
    // This ensures we transition to backoff mode even if queries are slow
    const checkInterval = setInterval(checkElapsedTime, 1000);

    return () => {
      clearInterval(checkInterval);
    };
  }, [pollStartTime, isSyncing, stopPolling]);

  return { hasUnsyncedConnections, hasIncompleteHistoricalSync, hasPotentialSyncIssue };
};
